import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Upload,
  message,
  Spin,
  Tag,
  Card,
  Modal,
  Input,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  getAssignedObservations,
  submitVerification,
} from '../../services/auditAPI';
import { uploadEvidenceToServer } from '../../services/operations/profileAPI';

const VerificationPage = () => {
  const user = useSelector((state) => state.profile.user);
  const [auditList, setAuditList] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // State for the verification response modal
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [remarkInput, setRemarkInput] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);

  // State for the rejection reason modal
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');


  useEffect(() => {
    const fetchData = async () => {
      if (!user?._id) return;

      try {
        const data = await getAssignedObservations(user._id);
        const filtered = data
          .map((audit) => ({
            ...audit,
            assignedObservations: audit.assignedObservations.filter(
              (obs) => obs.closureData?.status === 'Assigned'
            ),
          }))
          .filter((audit) => audit.assignedObservations.length > 0);

        setAuditList(filtered);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch assigned observations');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleAuditClick = (audit) => {
    setSelectedAudit(audit);
  };

  const handleBack = () => {
    setSelectedAudit(null);
    // Close any open modals when navigating back
    setResponseModalVisible(false);
    setRejectionModalVisible(false);
  };

  // Open the verification response modal
  const openResponseModal = (observation) => {
    setSelectedObservation(observation);
    setRemarkInput('');
    setEvidenceFile(null);
    setResponseModalVisible(true);
  };

  // Open the rejection reason modal
  const openRejectionModal = (reason) => {
    setRejectionReason(reason);
    setRejectionModalVisible(true);
  };

  const handleModalSubmit = async () => {
    if (!remarkInput.trim()) {
      return message.warning('Please enter your remarks');
    }

    try {
      setSubmitting(true);
      let fileUrl = null;

      if (evidenceFile) {
        fileUrl = await uploadEvidenceToServer(evidenceFile);
      }

      const verificationData = {
        observationId: selectedObservation._id,
        remarks: remarkInput,
        evidenceUrl: fileUrl,
      };

      await submitVerification(
        selectedAudit.auditId,
        user._id,
        verificationData
      );

      message.success('Response submitted');

      // Update the local state to reflect the submission status
      setSelectedAudit((prevAudit) => ({
        ...prevAudit,
        assignedObservations: prevAudit.assignedObservations.map((obs) =>
          obs._id === selectedObservation._id
            ? {
                ...obs,
                verificationData: {
                  ...obs.verificationData,
                  status: 'Submitted',
                },
                // Clear rejection reason if it was previously rejected and now submitted
                closureData: {
                  ...obs.closureData,
                  rejectionReason: undefined,
                }
              }
            : obs
        ),
      }));

      setResponseModalVisible(false);
    } catch (err) {
      console.error(err);
      message.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Very High':
      case 'High':
      case 1:
      case 2:
        return 'red';
      case 'Medium':
      case 3:
        return 'gold';
      case 4:
      case 5:
      case 'Low':
      case 'Very Low':
        return 'green';
      default:
        return 'transparent';
    }
  };

  const observationColumns = [
    {
      title: 'Observation',
      dataIndex: 'observation',
      key: 'observation',
    },
    {
      title: 'Element',
      dataIndex: 'element',
      key: 'element',
    },
    {
      title: 'Legal Standard',
      dataIndex: 'legalStandard',
      key: 'legalStandard',
    },
    {
      title: 'Recommendation',
      dataIndex: 'recommendation',
      key: 'recommendation',
    },
    {
      title: 'Risk',
      dataIndex: 'risk',
      key: 'risk',
      render: (risk) => (
        <div
          style={{
            backgroundColor: getRiskColor(risk),
            padding: '4px 8px',
            borderRadius: 4,
            color: 'white',
          }}
        >
          {risk}
        </div>
      ),
    },
    {
      title: 'Risk Score',
      dataIndex: 'riskScore',
      key: 'riskScore',
      render: (score) => (
        <div
          style={{
            backgroundColor: getRiskColor(score),
            padding: '4px 8px',
            borderRadius: 4,
            color: 'white',
          }}
        >
          {score}
        </div>
      ),
    },
    {
      title: 'Deadline',
      key: 'deadline',
      render: (_, record) =>
        record.closureData?.deadline ? (
          new Date(record.closureData.deadline).toLocaleDateString()
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const rejectedReason = record.closureData?.rejectionReason;
        const isSubmitted = record.verificationData?.status === 'Submitted';
        return isSubmitted ? (
          <Tag color="blue">Submitted</Tag>
        ) : (
          <div>
            {/* Tooltip on hover, Modal on click */}
            {rejectedReason && (
              <Tooltip title="Click to see rejection reason">
                <InfoCircleOutlined
                  style={{ marginRight: 8, color: 'red', cursor: 'pointer' }} // Added cursor style
                  onClick={() => openRejectionModal(rejectedReason)} // Open modal on click
                />
              </Tooltip>
            )}
            <Button type="primary" onClick={() => openResponseModal(record)}>
              Respond
            </Button>
          </div>
        );
      },
    },
  ];

  const auditListColumns = [
    {
      title: 'Audit Title',
      dataIndex: 'auditTitle',
      key: 'auditTitle',
    },
    {
      title: 'Status',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      render: (status) => <Tag color="orange">{status}</Tag>,
    },
    {
      title: 'Assigned Observations',
      key: 'assignedCount',
      render: (_, record) => record.assignedObservations?.length || 0,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleAuditClick(record)}>
          View Observations
        </Button>
      ),
    },
  ];

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4'>Verification Page</h2>
      {loading ? (
        <Spin tip="Loading observations..." />
      ) : selectedAudit ? (
        <>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          >
            Back to Audits
          </Button>
          <Card title={selectedAudit.auditTitle} bordered>
            <Table
              dataSource={selectedAudit.assignedObservations}
              rowKey="_id"
              columns={observationColumns}
              bordered
              scroll={{ x: true }}
              pagination={false}
            />
          </Card>
        </>
      ) : (
        <Table
          dataSource={auditList}
          rowKey="auditId"
          columns={auditListColumns}
          bordered
          pagination={false}
        />
      )}

      {/* Verification Response Modal */}
      <Modal
        title="Submit Verification"
        open={responseModalVisible}
        onCancel={() => setResponseModalVisible(false)}
        onOk={handleModalSubmit}
        confirmLoading={submitting}
        okText="Submit"
      >
        <p><strong>Remarks</strong></p>
        <Input.TextArea
          rows={4}
          value={remarkInput}
          onChange={(e) => setRemarkInput(e.target.value)}
          placeholder="Enter your remarks"
        />
        <div style={{ marginTop: 16 }}>
          <Upload
            beforeUpload={(file) => {
              setEvidenceFile(file);
              return false;
            }}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload Evidence</Button>
          </Upload>
        </div>
      </Modal>

      {/* Rejection Reason Modal */}
      <Modal
        title="Rejection Reason"
        open={rejectionModalVisible}
        onCancel={() => setRejectionModalVisible(false)}
        footer={null} // No footer needed for a read-only modal
      >
        <p>{rejectionReason}</p>
      </Modal>
    </div>
  );
};

export default VerificationPage;
